import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useParams } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { AdminProjects } from '@/components/admin/AdminProjects';
import { AdminProjectEdit } from '@/components/admin/AdminProjectEdit';
import { AdminHomeGallery } from '@/components/admin/AdminHomeGallery';
import { AdminMenuGallery } from '@/components/admin/AdminMenuGallery';
import { AdminClientPages } from '@/components/admin/AdminClientPages';
import { AdminClientPageEdit } from '@/components/admin/AdminClientPageEdit';
import { AdminAppearance } from '@/components/admin/AdminAppearance';
import { AdminMenuManager } from '@/components/admin/AdminMenuManager';
import { AdminSettings } from '@/components/admin/AdminSettings';
import { AdminProfile } from '@/components/admin/AdminProfile';
import { AdminAbout } from '@/components/admin/AdminAbout';
import { AdminStats } from '@/components/admin/AdminStats';
import { AdminFreePageEdit } from '@/components/admin/AdminFreePageEdit';
import { AdminPlans } from '@/components/admin/AdminPlans';
import { CmsTutorial } from '@/components/admin/CmsTutorial';
import { CmsSidebar, MobileMenuButton } from '@/components/admin/CmsSidebar';
import { CmsTopbar } from '@/components/admin/CmsTopbar';
import { GettingStarted } from '@/components/admin/GettingStarted';
import { StatusDashboard } from '@/components/admin/StatusDashboard';
import { CmsDashboard } from '@/components/admin/CmsDashboard';
import { useOnboardingChecklist } from '@/hooks/useOnboardingChecklist';
import { supabase } from '@/integrations/supabase/client';
import { CmsDashboardSkeleton } from '@/components/admin/CmsSkeletons';

const monoFont = "'IBM Plex Mono', monospace";

interface ProjectInfo {
  id: string;
  name: string;
  published: boolean;
  sectionName: string;
}

export default function AdminDashboard() {
  const { username: paramUsername } = useParams<{ username: string }>();
  const { username: tenantUsername } = useTenant();
  const username = tenantUsername || paramUsername;
  const { session, loading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [homeView, setHomeView] = useState<'dashboard' | 'getting-started'>(() => {
    const saved = localStorage.getItem('cms_home_view');
    return saved === 'getting-started' ? 'getting-started' : 'dashboard';
  });
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeProject, setActiveProject] = useState<ProjectInfo | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [initialTabSet, setInitialTabSet] = useState(false);
  const [sectionTabs, setSectionTabs] = useState<{ slug: string; name: string }[]>([]);
  const [verifiedOwner, setVerifiedOwner] = useState<boolean | null>(null);
  const [tokenProcessed, setTokenProcessed] = useState(false);

  // MUST be first useEffect — establishes session from URL tokens before auth guard runs
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (accessToken && refreshToken) {
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }).then(async () => {
        // Aguarda a sessão ser propagada
        await new Promise(resolve => setTimeout(resolve, 1000));
        window.history.replaceState({}, '', window.location.pathname);
        setTokenProcessed(true);
      });
    } else {
      setTokenProcessed(true);
    }
  }, []);

  const { items: checklistItems, loading: checklistLoading, completedCount, totalCount, allDone, siteName, refresh: refreshChecklist } = useOnboardingChecklist(session?.user?.id);

  // Set initial tab to dashboard (always default)
  useEffect(() => {
    if (checklistLoading || initialTabSet) return;
    setActiveTab('dashboard');
    setInitialTabSet(true);
  }, [checklistLoading, initialTabSet]);

  // Verify the authenticated user owns this username
  useEffect(() => {
    if (!session || !username) return;
    const verify = async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', session.user.id)
        .maybeSingle();
      const isOwner = data?.username === username;
      setVerifiedOwner(isOwner);
      if (isOwner) {
        // fire-and-forget: registra último acesso ao CMS
        supabase
          .from('user_profiles')
          .update({ last_access_at: new Date().toISOString() })
          .eq('user_id', session.user.id);
      }
    };
    verify();
  }, [session, username]);

  useEffect(() => {
    if (!session) return;
    const loadSections = async () => {
      const [{ data: menuData }, { data: projectData }] = await Promise.all([
        supabase.from('menu_items').select('label, section_slug').eq('user_id', session.user.id).not('section_slug', 'is', null).order('sort_order'),
        supabase.from('projects').select('section_slug').eq('user_id', session.user.id),
      ]);

      const tabs: { slug: string; name: string }[] = [];
      const slugSet = new Set<string>();

      if (menuData) {
        (menuData as any[]).filter(d => d.section_slug).forEach(d => {
          if (!slugSet.has(d.section_slug)) {
            tabs.push({ slug: d.section_slug, name: d.label });
            slugSet.add(d.section_slug);
          }
        });
      }

      // Add orphaned sections
      if (projectData) {
        (projectData as any[]).forEach(p => {
          if (!slugSet.has(p.section_slug)) {
            tabs.push({ slug: p.section_slug, name: p.section_slug.charAt(0).toUpperCase() + p.section_slug.slice(1) });
            slugSet.add(p.section_slug);
          }
        });
      }

      setSectionTabs(tabs);
    };
    loadSections();
  }, [session]);

  useEffect(() => {
    if (!activeProjectId) {
      setActiveProject(null);
      return;
    }
    // For project sections, load project info
    if (activeTab !== 'aprovacao') {
      const loadProject = async () => {
        const { data } = await supabase
          .from('projects')
          .select('id, name, published, section_slug')
          .eq('id', activeProjectId)
          .single();
        if (data) {
          const section = sectionTabs.find(s => s.slug === (data as any).section_slug);
          setActiveProject({
            id: data.id,
            name: data.name,
            published: data.published,
            sectionName: section?.name || '',
          });
        }
      };
      loadProject();
    } else {
      setActiveProject(null);
    }
  }, [activeProjectId, sectionTabs, activeTab]);

  useEffect(() => {
    const seen = localStorage.getItem('cms-tutorial-seen');
    if (!seen) {
      setShowTutorial(true);
      localStorage.setItem('cms-tutorial-seen', '1');
    }
  }, []);

  // Listen for cms-navigate events from menu manager "EDITAR CONTEÚDO"
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.tab) {
        setActiveTab(detail.tab);
        setActiveProjectId(detail.projectId || null);
      }
    };
    window.addEventListener('cms-navigate', handler);
    return () => window.removeEventListener('cms-navigate', handler);
  }, []);

  const handleSelectTab = (tab: string, projectId?: string | null) => {
    setActiveTab(tab);
    setActiveProjectId(projectId || null);
  };

  const handleProjectStatusChange = async () => {
    if (!activeProjectId || activeTab === 'aprovacao') return;
    const { data } = await supabase
      .from('projects')
      .select('id, name, published, section_slug')
      .eq('id', activeProjectId)
      .single();
    if (data) {
      const section = sectionTabs.find(s => s.slug === (data as any).section_slug);
      setActiveProject({
        id: data.id,
        name: data.name,
        published: data.published,
        sectionName: section?.name || '',
      });
    }
  };

  const loginPath = tenantUsername ? '/login' : `/${username}/login`;

  // Wait for token processing before auth guard
  if (!tokenProcessed) {
    return <CmsDashboardSkeleton />;
  }

  // If auth loading is done and no session, redirect immediately
  if (!loading && !session) {
    return <Navigate to={loginPath} replace />;
  }

  // Still loading auth, verifying ownership, or resolving initial tab
  if (loading || verifiedOwner === null || !initialTabSet) {
    return <CmsDashboardSkeleton />;
  }

  if (verifiedOwner === false) return <Navigate to={loginPath} replace />;

  // Breadcrumb
  const breadcrumb: string[] = [];
  if (activeTab === 'dashboard') breadcrumb.push(homeView === 'dashboard' ? 'Dashboard' : 'Início');
  else if (activeTab === 'home') breadcrumb.push('Home');
  else if (activeTab === 'about') breadcrumb.push('About');
  else if (activeTab === 'aprovacao') {
    breadcrumb.push('Aprovação');
  }
  else if (activeTab === 'menu') breadcrumb.push('Menu');
  else if (activeTab === 'freepage') breadcrumb.push('Página');
  else if (activeTab === 'menu-gallery') breadcrumb.push('Galeria');
  else if (activeTab === 'planos') breadcrumb.push('Planos');
  else if (activeTab === 'aparencia') breadcrumb.push('Aparência');
  else if (activeTab === 'estatisticas') breadcrumb.push('Estatísticas');
  else if (activeTab === 'configuracoes') breadcrumb.push('Configurações');
  else if (activeTab === 'perfil') breadcrumb.push('Perfil');
  else {
    const section = sectionTabs.find(s => s.slug === activeTab);
    if (section) {
      breadcrumb.push(section.name);
      if (activeProject) breadcrumb.push(activeProject.name);
    }
  }

  const handleChecklistNavigate = (tab: string) => {
    setActiveTab(tab);
    setActiveProjectId(null);
  };

  const handleHomeViewChange = (view: 'dashboard' | 'getting-started') => {
    setHomeView(view);
    localStorage.setItem('cms_home_view', view);
  };

  const monoFontLocal = "'IBM Plex Mono', monospace";

  const homeToggle = activeTab === 'dashboard' ? (
    <div style={{ display: 'flex', gap: 0, marginBottom: 24 }}>
      <button
        onClick={() => handleHomeViewChange('dashboard')}
        style={{
          fontFamily: monoFontLocal, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' as const,
          padding: '12px 24px', border: 'none', background: 'none', cursor: 'pointer',
          color: homeView === 'dashboard' ? '#F7F6F2' : 'rgba(247,246,242,0.4)',
          borderBottom: homeView === 'dashboard' ? '1px solid #F7F6F2' : '1px solid transparent',
        }}
      >
        DASHBOARD
      </button>
      <button
        onClick={() => handleHomeViewChange('getting-started')}
        style={{
          fontFamily: monoFontLocal, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' as const,
          padding: '12px 24px', border: 'none', background: 'none', cursor: 'pointer',
          color: homeView === 'getting-started' ? '#F7F6F2' : 'rgba(247,246,242,0.4)',
          borderBottom: homeView === 'getting-started' ? '1px solid #F7F6F2' : '1px solid transparent',
        }}
      >
        INÍCIO
      </button>
    </div>
  ) : null;

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {homeToggle}
            {homeView === 'getting-started' ? (
              <GettingStarted
                items={checklistItems}
                completedCount={completedCount}
                totalCount={totalCount}
                siteName={siteName}
                onNavigate={handleChecklistNavigate}
                onOpenTutorial={() => setShowTutorial(true)}
              />
            ) : (
              <CmsDashboard
                onNavigate={handleChecklistNavigate}
                username={username || ''}
              />
            )}
          </>
        );
      case 'home':
        return <AdminHomeGallery />;
      case 'about':
        return <AdminAbout />;
      case 'aprovacao':
        if (activeProjectId) {
          return (
            <AdminClientPageEdit
              pageId={activeProjectId}
              onBack={() => setActiveProjectId(null)}
            />
          );
        }
        return (
          <AdminClientPages
            onSelectPage={(pageId) => setActiveProjectId(pageId)}
          />
        );
      case 'menu':
        return <AdminMenuManager />;
      case 'freepage':
        if (activeProjectId) {
          return (
            <AdminFreePageEdit
              pageId={activeProjectId}
              onBack={() => { setActiveTab('menu'); setActiveProjectId(null); }}
            />
          );
        }
        return null;
      case 'menu-gallery':
        if (activeProjectId) {
          return (
            <AdminMenuGallery
              menuItemId={activeProjectId}
              onBack={() => { setActiveTab('menu'); setActiveProjectId(null); }}
            />
          );
        }
        return null;
      case 'aparencia':
        return <AdminAppearance />;
      case 'planos':
        return <AdminPlans />;
      case 'estatisticas':
        return <AdminStats />;
      case 'configuracoes':
        return <AdminSettings />;
      case 'perfil':
        return <AdminProfile />;
      default: {
        const section = sectionTabs.find(s => s.slug === activeTab);
        if (!section) return null;

        if (activeProjectId) {
          return (
            <AdminProjectEdit
              projectId={activeProjectId}
              onBack={() => {
                setActiveProjectId(null);
                window.dispatchEvent(new CustomEvent('cms-projects-updated'));
              }}
            />
          );
        }

        return (
          <AdminProjects
            key={activeTab}
            sectionSlug={activeTab}
            sectionName={section.name}
            onSelectProject={(projectId) => setActiveProjectId(projectId)}
            onDataChange={() => {
              window.dispatchEvent(new CustomEvent('cms-projects-updated'));
            }}
          />
        );
      }
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', backgroundColor: '#0A0A0A', overflow: 'hidden' }}>
      {showTutorial && (
        <CmsTutorial
          onNavigateTab={(tab) => { setActiveTab(tab); setActiveProjectId(null); }}
          onClose={() => setShowTutorial(false)}
        />
      )}

      <div className="md:hidden">
        {!sidebarOpen && <MobileMenuButton onClick={() => setSidebarOpen(true)} />}
      </div>

      <CmsSidebar
        activeTab={activeTab}
        activeProjectId={activeProjectId}
        onSelectTab={handleSelectTab}
        onOpenTutorial={() => setShowTutorial(true)}
        onOpenGettingStarted={() => { setActiveTab('dashboard'); setHomeView('getting-started'); localStorage.setItem('cms_home_view', 'getting-started'); setActiveProjectId(null); refreshChecklist(); }}
        onSignOut={signOut}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 240, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="max-md:!left-0"
      >
        <CmsTopbar
          breadcrumb={breadcrumb}
          projectId={activeTab !== 'aprovacao' ? activeProjectId : null}
          projectPublished={activeProject?.published ?? null}
          onProjectStatusChange={handleProjectStatusChange}
        />

        <div
          className={`flex-1 cms-dark ${activeTab === 'aparencia' ? 'p-0 overflow-hidden' : 'p-8 overflow-y-auto'}`}
          style={{ backgroundColor: '#0A0A0A', color: 'rgba(247,246,242,0.85)' }}
        >
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
